import { useEffect } from 'react'

import { z } from 'zod'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  Textarea,
} from '@fortem-monorepo/react-ui'
import { PnlAttributions } from '@fortem-monorepo/web-sdk'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { useRefreshAttributionsMutation } from '@/pages/operations/pnl-attributions/mutations'

const refreshAttributionsSchema = z.object({
  fund_name: z.string().min(5).max(5),
  value_date: z.iso.date(),
  reason: z.string().min(1, 'You must provide a reason to reset the attributions.').nonempty(),
})

export interface RefreshAttributionsDialogProps extends Partial<ConfirmationDialogProps> {
  mutationParams: PnlAttributions.StartAttributionsParams
}

export const RefreshAttributionsDialog: React.FC<RefreshAttributionsDialogProps> = ({ mutationParams, ...props }) => {
  const form = useFormWithSchema(
    {
      mode: 'all',
      defaultValues: { fund_name: mutationParams.fund_name, value_date: mutationParams.value_date },
    },
    refreshAttributionsSchema,
  )

  const refreshAttributionsMutation = useRefreshAttributionsMutation({
    fund_name: mutationParams.fund_name,
    value_date: mutationParams.value_date,
  })

  useEffect(() => {
    form.trigger()
  }, [])

  const onSubmit = form.handleSubmit(async (data) => {
    await refreshAttributionsMutation.mutateAsync(data)
    props.onClose?.()
  })

  return (
    <ConfirmationDialog
      {...props}
      action={onSubmit}
      title={`Refresh Attributions for ${mutationParams.fund_name} on ${mutationParams.value_date}`}
      description={'This will create a fresh batch of attributions, any progress made so far will be lost!'}
      pending={refreshAttributionsMutation.isPending}
      valid={form.formState.isValid}
    >
      <Form {...form}>
        <form>
          <FormFieldRow>
            <FormField
              name="reason"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-1">
                      <FormLabel>Reason</FormLabel>
                      <FormError />
                    </div>
                    <Textarea value={field.value} onChange={field.onChange} />
                  </FormItem>
                )
              }}
            />
          </FormFieldRow>
        </form>
      </Form>
    </ConfirmationDialog>
  )
}
